import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Form,
  Button,
  Select,
  Switch,
  Space,
  Typography,
  Tooltip,
  notification,
} from 'antd';

import {
  DynamicFormField,
  DynamicFormFieldItem,
  PercentInput,
} from 'components';
import {
  selectors,
  updateAllowIndividualBenchmark,
  updateSessionBenchmarkComposition,
  BenchmarkComposition as BenchmarkCompositionType,
  requestBenchmarkPreview,
  HISTORICAL_SCENARIO,
  clearBenchmarkPreview,
} from 'state';
import { InfoCircleOutlined } from '@ant-design/icons';
import BenchmarkPreviewModal from './BenchmarkPreviewModal';
import { useModal } from 'services';
import ShowIndexFundsModal from './IndexFundsTable';
import { TrainerSessionContext } from '../../TrainerSessionContext';

const hasValidCompositionWeights = (
  composition: BenchmarkCompositionType[] = []
) => {
  if (!composition.length) {
    notification.error({
      message: 'Benchmark composition cannot be empty.',
    });
    return false;
  }

  const hasInvalidWeight = composition.some(
    item => item.weight == null || item.weight < 1 || item.weight > 100
  );
  if (hasInvalidWeight) {
    notification.error({
      message: 'Each benchmark weight must be between 1% and 100%.',
    });
    return false;
  }

  const totalWeight = composition.reduce((sum, item) => sum + item.weight, 0);
  if (Math.abs(totalWeight - 100) > 0.01) {
    notification.error({
      message: `Benchmark weights must add up to 100%. Current total: ${totalWeight.toFixed(
        2
      )}%.`,
    });
    return false;
  }

  return true;
};

const BenchmarkIndexComposition: React.FC = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const trainerContext = useContext(TrainerSessionContext);

  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [FundsModal] = useModal(ShowIndexFundsModal);

  const session = useSelector(selectors.activeSession);
  const isHistorical = session.scenario_type === HISTORICAL_SCENARIO;
  const showPreviewButoon = isHistorical && !session.allow_individual_benchmark;
  const eligibleOptions = session.eligible_benchmark_options;

  const compositeList = useMemo(
    () => [
      ...(eligibleOptions?.index_funds || []),
      ...(eligibleOptions?.equities || []).map(item => ({
        ...item,
        issuer_name: '',
      })),
    ],
    [eligibleOptions]
  );

  useEffect(() => {
    // whenever the backend clears or replaces the compositions,
    // push those new values into the form
    form.setFieldsValue({ composition: benchmark_compositions });
  }, [session.benchmark_compositions, form]);

  const hidePreviewModal = () => {
    setShowPreviewModal(false);
  };

  useEffect(() => {
    if (session.benchmark_preview) {
      setPreviewLoading(false);
      setShowPreviewModal(true);
    } else {
      setShowPreviewModal(false);
    }
  }, [session.benchmark_preview]);
  useEffect(() => {
    // Cleanup function
    return () => {
      dispatch(clearBenchmarkPreview({}));
    };
  }, [dispatch]);

  const benchmark_compositions = session.benchmark_compositions.map(item => ({
    ...item,
    fund_or_asset_ric: item.ric,
    weight: item.weight * 100,
  }));

  const onFinish = () => {
    const rawComposition = form.getFieldValue('composition') || [];
    if (!hasValidCompositionWeights(rawComposition)) {
      return;
    }

    const composition = rawComposition.map(
      (item: BenchmarkCompositionType) => ({
        ...item,
        weight: item.weight / 100,
      })
    );

    dispatch(updateSessionBenchmarkComposition({ composition }));
  };
  return (
    <Space size={15} direction="vertical" style={{ display: 'flex' }}>
      <Space size={10}>
        <Switch
          checked={session.allow_individual_benchmark}
          onClick={value => {
            dispatch(updateAllowIndividualBenchmark({ allow: value }));
          }}
        />
        <Typography.Text>
          <span style={{ whiteSpace: 'nowrap' }}>
            Allow individual benchmark
          </span>
        </Typography.Text>
        <Tooltip
          title={
            'Enable individual benchmark if you want every team to set up their own benchmark'
          }
        >
          <InfoCircleOutlined />
        </Tooltip>
      </Space>

      <Form
        form={form}
        onFinish={onFinish}
        initialValues={{ composition: benchmark_compositions }}
      >
        {!session.allow_individual_benchmark && (
          <DynamicFormField
            name="composition"
            addNewText="Add Fund"
            listClassName="set3b-bench-complist"
            onClick={() =>
              trainerContext.setActiveKey('available_benchmark_options')
            }
            renderField={field => (
              <>
                <DynamicFormFieldItem
                  field={field}
                  label="Fund"
                  name="fund_or_asset_ric"
                  missingMessage="Missing Fund"
                >
                  <Select
                    onFocus={() =>
                      trainerContext.setActiveKey('available_benchmark_options')
                    }
                    showSearch
                    allowClear
                    optionFilterProp="children"
                    style={{ width: 150 }}
                  >
                    {compositeList.map(item => (
                      <Select.Option key={item.ric} value={item.ric}>
                        {item.ric}
                      </Select.Option>
                    ))}
                  </Select>
                </DynamicFormFieldItem>

                <DynamicFormFieldItem
                  field={field}
                  label="Weight"
                  name="weight"
                  missingMessage="Missing Weight"
                >
                  <PercentInput min={1} max={100} style={{ width: 75 }} />
                </DynamicFormFieldItem>
              </>
            )}
          />
        )}

        <Form.Item>
          {showPreviewButoon && (
            <Button
              // type="primary"
              style={{ marginRight: 10 }}
              loading={previewLoading}
              onClick={() => {
                const rawComposition = form.getFieldValue('composition') || [];
                if (!hasValidCompositionWeights(rawComposition)) {
                  return;
                }

                const composition = rawComposition.map(
                  (item: BenchmarkCompositionType) => ({
                    ...item,
                    weight: item.weight / 100,
                  })
                );
                // dispatch(updateSessionBenchmarkComposition({ composition }));
                setPreviewLoading(true);
                dispatch(requestBenchmarkPreview({ composition }));
              }}
            >
              Preview
            </Button>
            // showFundsModal
          )}
          {/*<Button onClick={() => showFundsModal({})}>Available Funds</Button>*/}
          <Button
            type="primary"
            htmlType="submit"
            style={{ position: 'absolute', right: 0 }}
          >
            Save
          </Button>
        </Form.Item>
      </Form>
      {showPreviewModal && (
        <BenchmarkPreviewModal
          visible={showPreviewModal}
          hide={hidePreviewModal}
        />
      )}
      {FundsModal}
    </Space>
  );
};

export default BenchmarkIndexComposition;
